import {
  PencilEdit01Icon,
  StarIcon
} from '@hugeicons/react'
import _ from 'lodash'

import { truncateName } from '@/shared/lib/string/truncate-name'
import { Avatar } from '@/shared/ui/Avatar'
import { DismissButton } from '@/shared/ui/DismissButton'
import { Link } from '@/shared/ui/Link'

import { useAppMenu } from './AppMenuContext'
import { ACCOUNT_PATHS } from '@/shared/lib/routing/account'

type AppMenuProfileHeaderProps = {
  user: { name?: string; email?: string; avatar?: { file?: { url?: string } } } | null
  hasAvatar: boolean
  avatar: unknown
  basePathWithId: string
  title: { name?: string; level?: number } | string | null
}

export function AppMenuProfileHeader({
  user,
  hasAvatar,
  avatar,
  basePathWithId,
  title
}: AppMenuProfileHeaderProps) {
  const { close } = useAppMenu()

  const titleName =
    typeof title === 'string' ? title : (title?.name ?? null)
  const titleLevel =
    typeof title === 'object' && title && 'level' in title
      ? title.level
      : null

  return (
    <div
      className="relative overflow-hidden px-4 pt-3.5 pb-8 text-white"
      style={{
        background:
          'linear-gradient(160deg, color-mix(in srgb, var(--color-brand) 88%, #fff) 0%, var(--color-brand) 45%, color-mix(in srgb, var(--color-brand) 80%, #000) 100%)'
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-45"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(255,255,255,0.55) 1px, transparent 1px), radial-gradient(circle, rgba(255,255,255,0.35) 1px, transparent 1px)',
          backgroundSize: '48px 48px, 72px 72px',
          backgroundPosition: '12px 18px, 36px 8px'
        }}
        aria-hidden
      />
      <svg
        className="pointer-events-none absolute top-5 right-3 h-[86px] w-[72px]"
        viewBox="0 0 80 96"
        fill="none"
        aria-hidden
      >
        <path
          d="M40 8C28 28 18 48 22 68L40 88L58 68C62 48 52 28 40 8Z"
          fill="rgba(255,255,255,0.22)"
        />
        <circle cx="40" cy="36" r="8" fill="rgba(255,255,255,0.35)" />
        <path
          d="M28 72L22 88M52 72L58 88"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </svg>

      <DismissButton
        onClick={close}
        aria-label="Fechar menu"
        className="absolute top-3.5 right-3.5 z-[3]"
      />

      <div className="relative z-[1] flex items-center gap-3.5 pt-8 pr-[4.5rem]">
        <Link
          href={`${basePathWithId}${ACCOUNT_PATHS.avatar}`}
          className="relative shrink-0"
          onClick={close}
        >
          {hasAvatar ? (
            <div className="h-[76px] w-[76px] overflow-hidden rounded-full border-[3px] border-white bg-white">
              <Avatar config={avatar} showFullBody={false} className="" />
            </div>
          ) : (
            <img
              src={_.get(user, 'avatar.file.url') || '/placeholders/user.svg'}
              alt=""
              className="block h-[76px] w-[76px] rounded-full border-[3px] border-white object-cover"
            />
          )}
          <span className="absolute -right-0.5 -bottom-0.5 flex h-[26px] w-[26px] items-center justify-center rounded-full border-2 border-white bg-white shadow-[0_2px_6px_rgba(0,0,0,0.12)]">
            <PencilEdit01Icon size={12} color="var(--color-brand)" />
          </span>
        </Link>

        <div className="flex min-w-0 flex-col items-start gap-0.5">
          <h2 className="m-0 text-left text-lg leading-tight font-extrabold">
            {truncateName(_.get(user, 'name', ''), 24)}
          </h2>
          <p className="m-0 text-left text-xs text-white/82">
            {_.get(user, 'email', '')}
          </p>

          {titleName ? (
            <span
              className="mt-0.5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-2xs leading-tight font-bold"
              style={{
                background: 'color-mix(in srgb, var(--color-brand) 55%, #000)'
              }}
            >
              <StarIcon size={12} variant="solid" />
              {titleLevel != null ? `Nível ${titleLevel} • ` : ''}
              {titleName}
            </span>
          ) : null}
        </div>
      </div>

      <svg
        className="absolute right-0 bottom-[-1px] left-0 z-[2] h-7 w-full"
        viewBox="0 0 400 28"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          d="M0,20 C80,4 160,32 240,14 C300,2 360,24 400,12 L400,28 L0,28 Z"
          fill="#fff"
        />
      </svg>
    </div>
  )
}
